import {
  Factory,
  Scales,
  Thermometer,
  ChartBar,
  GearSix,
  Broadcast,
} from "@phosphor-icons/react";
import { subdomainImage } from "./utils";

const slug = "metals";

/** Metals — metals.bizlem.com */
const metalsConfig = {
  slug,
  name: "Metals",
  shortName: "Metals",
  host: "metals.bizlem.com",
  icon: Factory,
  theme: {
    primary: "bg-rose-500",
    primaryHover: "hover:bg-rose-600",
    primaryText: "text-rose-600",
    primaryBorder: "border-rose-500",
    tint: "bg-rose-50",
    heroGradient: "from-rose-700 via-rose-500 to-rose-400",
    cream: "bg-rose-50/40",
    badge: "bg-rose-50 border-rose-200 text-rose-700",
    ring: "ring-rose-100",
  },
  hero: {
    badge: "Industrial Intelligence — Metals & Steel",
    headline: ["Precision at Temperature.", "Intelligence at Scale."],
    highlightLine: 1,
    description:
      "Optimize production, quality, and energy across integrated steel plants and heavy metals operations with AI-first industrial intelligence.",
    image: "https://images.unsplash.com/photo-1565008447742-97f6f38c985c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    ctaPrimary: "Request Demo",
    ctaSecondary: "Explore Solutions",
  },
  about: {
    label: "WHAT WE DO",
    heading: "Enabling metals producers with smart, connected operations",
    body: "From blast furnaces to rolling mills, our platform connects production, quality, energy, and maintenance into one intelligent system for measurable throughput and yield gains.",
  },
  modules: {
    label: "MODULES",
    heading: "Integrated Modules. One Intelligent Plant.",
    body: "Purpose-built modules for metals operations that work together — so production, quality, energy, and maintenance run on a single connected platform.",
    items: [
      { icon: Factory, title: "Production Optimization", body: "Real-time throughput analytics, bottleneck detection, and schedule optimization across the value chain.", iconBg: "bg-rose-100 text-rose-700" },
      { icon: Scales, title: "Quality Prediction", body: "AI-driven quality forecasting and defect prevention across casting, rolling, and finishing lines.", iconBg: "bg-amber-100 text-amber-700" },
      { icon: Thermometer, title: "Energy Management", body: "Monitor and optimize energy consumption across furnaces, utilities, and auxiliary systems.", iconBg: "bg-orange-100 text-orange-700" },
      { icon: ChartBar, title: "Blast Furnace Analytics", body: "Advanced analytics for burden optimization, hot metal chemistry, and furnace health monitoring.", iconBg: "bg-blue-100 text-blue-700" },
      { icon: GearSix, title: "Predictive Maintenance", body: "Condition monitoring and failure prediction for critical rotating and process equipment.", iconBg: "bg-emerald-100 text-emerald-700" },
      { icon: Broadcast, title: "Unified Command Center", body: "Single-pane operations view for plant leadership with real-time KPIs and alerts.", iconBg: "bg-violet-100 text-violet-700" },
    ],
  },
  solutions: {
    label: "OPERATIONS",
    heading: "Built for every stage of metals production",
    items: [
      {
        title: "Integrated Steel Plants",
        points: ["Blast furnace intelligence", "Coke oven optimization", "Rolling mill analytics", "Energy & emissions tracking"],
        image: "https://images.unsplash.com/photo-1565008447742-97f6f38c985c?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
      },
      {
        title: "Mini Mills & EAF",
        points: ["Scrap optimization", "Melt shop analytics", "Continuous casting intelligence", "Yield improvement workflows"],
        image: subdomainImage(slug, "mini-mills-eaf.png"),
      },
      {
        title: "Downstream Processing",
        points: ["Coating line quality control", "Inventory & logistics optimization", "Customer order fulfillment", "Batch traceability"],
        image: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
      },
      {
        title: "Maintenance & Reliability",
        points: ["Asset health monitoring", "Spare parts optimization", "Shutdown planning", "Reliability-centered maintenance"],
        image: subdomainImage(slug, "maintenance-reliability.png"),
      },
    ],
  },
  whyChoose: {
    label: "WHY CHOOSE US",
    heading: "Built for Scale. Designed for Simplicity.",
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
    items: [
      "One unified platform for all metals plant operations",
      "Scalable from single lines to multi-plant enterprises",
      "Deep domain expertise in steel and heavy metals",
      "Proven ROI through yield, energy, and uptime improvements",
    ],
  },
  footerTagline:
    "AI-Powered Industrial Intelligence for Metals & Steel — connecting production, quality, and energy into measurable results.",
  contactIndustry: "Metals & Steel",
};

export default metalsConfig;
